import { useState, useEffect, useCallback, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import {
  addToQueue,
  getQueue,
  getQueueCount,
  removeFromQueue,
  updateInQueue,
  clearQueue,
} from './offlineStorage';
import type { QueuedMutation, OfflineState } from './types';

export type FreshHeadersFn = () => Record<string, string>;

export interface UseOfflineQueueReturn extends OfflineState {
  addMutation: (mutation: Omit<QueuedMutation, 'id' | 'queuedAt' | 'retries' | 'maxRetries'>) => Promise<void>;
  processQueue: () => Promise<void>;
  clearAll: () => Promise<void>;
}

export function useOfflineQueue(isOnline: boolean, getFreshHeaders?: FreshHeadersFn): UseOfflineQueueReturn {
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const processingRef = useRef(false);
  const wasOnlineRef = useRef(isOnline);

  const refreshCount = useCallback(async () => {
    const count = await getQueueCount();
    setPendingCount(count);
  }, []);

  const addMutation = useCallback(
    async (mutation: Omit<QueuedMutation, 'id' | 'queuedAt' | 'retries' | 'maxRetries'>) => {
      const entry: QueuedMutation = {
        ...mutation,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        queuedAt: new Date().toISOString(),
        retries: 0,
        maxRetries: 5,
      };
      await addToQueue(entry);
      await refreshCount();
    },
    [refreshCount],
  );

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    setIsSyncing(true);
    setLastError(null);

    try {
      const queue = await getQueue();
      if (queue.length === 0) {
        setIsSyncing(false);
        processingRef.current = false;
        return;
      }

      for (const mutation of queue) {
        if (mutation.retries >= mutation.maxRetries) {
          await removeFromQueue(mutation.id);
          notifications.show({
            title: 'Sync Failed',
            message: `"${mutation.label || mutation.url}" exceeded max retries and was removed.`,
            color: 'red',
            autoClose: 8000,
          });
          continue;
        }

        try {
          // Use fresh auth headers from the current session instead of
          // the stale ones captured when the mutation was queued (token
          // may have expired since then).
          const freshHeaders = getFreshHeaders?.() ?? {};
          const response = await fetch(mutation.url, {
            method: mutation.method,
            headers: {
              'Content-Type': 'application/json',
              ...mutation.headers,
              ...freshHeaders,
            },
            body: mutation.data != null ? JSON.stringify(mutation.data) : undefined,
            credentials: 'include',
          });

          if (response.ok) {
            await removeFromQueue(mutation.id);
            notifications.show({
              title: 'Synced',
              message: mutation.label || `${mutation.method} ${mutation.url}`,
              color: 'green',
              autoClose: 3000,
            });
          } else {
            await updateInQueue(mutation.id, { retries: mutation.retries + 1 });
            setLastError(`${mutation.method} ${mutation.url} failed (${response.status})`);
          }
        } catch {
          await updateInQueue(mutation.id, { retries: mutation.retries + 1 });
          setLastError(`Network error syncing ${mutation.label || mutation.url}`);
          break; // Stop processing if network is still down
        }
      }

      setLastSyncAt(new Date().toISOString());
    } finally {
      await refreshCount();
      setIsSyncing(false);
      processingRef.current = false;
    }
  }, [refreshCount, getFreshHeaders]);

  const clearAll = useCallback(async () => {
    await clearQueue();
    await refreshCount();
  }, [refreshCount]);

  // Auto-sync on offline → online transition
  useEffect(() => {
    if (isOnline && !wasOnlineRef.current) {
      processQueue();
    }
    wasOnlineRef.current = isOnline;
  }, [isOnline, processQueue]);

  // Poll queue count periodically
  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, 5000);
    return () => clearInterval(interval);
  }, [refreshCount]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncAt,
    lastError,
    addMutation,
    processQueue,
    clearAll,
  };
}
